import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import moment from 'moment';

// Material Imports
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatMomentDateModule, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MatIconModule } from '@angular/material/icon';
import { formatDateToYYYYMMDD } from '../utils/date.utils';
import { ICustomCellEditorParams } from '../models/custom-date-picker.params';

export const MY_FORMATS = {
  parse: {
    dateInput: 'YYYY-MM-DD',
  },
  display: {
    dateInput: 'YYYY-MM-DD',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'lib-custom-date-picker',
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatMomentDateModule,
    MatIconModule
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: MAT_DATE_LOCALE, useValue: 'en-short-weekdays' }
  ],
  templateUrl: './custom-date-picker.component.html',
  styleUrls: ['./custom-date-picker.component.scss']
})
export class CustomDatePickerComponent implements ICellEditorAngularComp {
  private params!: ICustomCellEditorParams;
  public value!: string | null;
  public cellWidth = 0;
  public cellHeight = 0;
  public minDate: moment.Moment | null = null;
  public maxDate: moment.Moment | null = null;

  @ViewChild('input') private readonly input!: ElementRef<HTMLInputElement>;

  agInit(params: ICustomCellEditorParams): void {
    this.params = params;
    this.value = params.value;
    this.minDate = params.minDate ? moment(params.minDate) : null;
    this.maxDate = params.maxDate ? moment(params.maxDate) : null;

    const cell = params.eGridCell;
    // Adjust size for better fit within the cell
    this.cellWidth = cell.clientWidth - 30;
    this.cellHeight = cell.clientHeight - 10;
  }

  getValue(): string | null {
    return this.value;
  }

  isPopup(): boolean {
    return true;
  }

  onDateSelected(momentDate: moment.Moment): void {
    this.value = formatDateToYYYYMMDD(momentDate.toDate());
    this.params.api.stopEditing();
  }

  public formatDateOnInput(): void {
    if (!this.value) {
      return;
    }

    const originalValue = this.value;
    let formattedValue = originalValue.replace(/\D/g, '').substring(0, 8);

    if (formattedValue.length > 4) {
      formattedValue = `${formattedValue.substring(0, 4)}-${formattedValue.substring(4)}`;
    }
    if (formattedValue.length > 7) {
      formattedValue = `${formattedValue.substring(0, 7)}-${formattedValue.substring(7)}`;
    }
    
    if (originalValue !== formattedValue) {
      this.value = formattedValue;
    }
  }

  // Validates and formats the input on blur or enter
  public validateAndFormatInput(): void {
    if (!this.value) {
      return;
    }

    // YYYY and YYYY-MM are invalid and should clear the input.
    const yearOnlyRegex = /^\d{4}$/;
    const yearMonthOnlyRegex = /^\d{4}-\d{2}$/;

    if (yearOnlyRegex.test(this.value) || yearMonthOnlyRegex.test(this.value)) {
      this.value = '';
      return;
    }

    const formats = ['YYYY-MM-DD', 'YYYYMMDD'];
    const m = moment(this.value, formats);
    if (m.isValid()) {
      this.value = m.format('YYYY-MM-DD');
    } else {
      // Revert to original value if input is invalid
      this.value = this.params.value;
    }
  }

  public onKeyDown(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'Tab', 'Enter', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (allowedKeys.includes(event.key)) {
      return;
    }

    // Allow numbers only since dashes are auto-inserted
    if (!/\d/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const currentValue = this.input.nativeElement.value;
    const selectionStart = this.input.nativeElement.selectionStart;
    const selectionEnd = this.input.nativeElement.selectionEnd;

    // Prevent typing more than 10 characters if there's no selection
    if (currentValue.length >= 10 && selectionStart === selectionEnd) {
      event.preventDefault();
    }
  }

  public clearInput(event: Event): void {
    event.stopPropagation();
    this.value = null;
    this.input.nativeElement.focus();
    this.input.nativeElement.blur(); // Trigger blur to notify AG Grid of the change
  }
}
